// src/components/Fees.jsx
import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import StarIcon from "@mui/icons-material/Star";
import RedeemIcon from "@mui/icons-material/Redeem";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import SecurityIcon from "@mui/icons-material/Security";

// ✅ SEO hooks
import { useSEO, useJsonLD } from "./SEO";

const BG = "#263238";
const SURFACE = "rgba(38,50,56,0.85)";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";
const ACCENT = "#F0B90B";

// ======= Fee constants =======
const SPOT_MAKER = 0.0;
const SPOT_TAKER = 0.0;
const FUTURES_MAKER = 0.0;
const FUTURES_TAKER = 0.0;
const AI_TRADE_FEE_DESC =
  "Applied per AI-executed order (shown before confirm)";

// Flat withdraw fee: 5 USDT for all listed assets (incl. LTC)
// ✅ ETA values exactly as requested
const WITHDRAW_ROWS = [
  { asset: "USDT (TRC20)", network: "TRON", cgId: "tether", eta: "≈ 1–3 min" },
  {
    asset: "USDT (ERC20)",
    network: "Ethereum",
    cgId: "tether",
    eta: "≈ 3–10 min",
  },
  { asset: "BTC", network: "Bitcoin", cgId: "bitcoin", eta: "≈ 10–60 min" },
  { asset: "ETH", network: "Ethereum", cgId: "ethereum", eta: "≈ 5–20 min" },
  { asset: "LTC", network: "Litecoin", cgId: "litecoin", eta: "≈ 5–20 min" },
];

// Helper: format numbers safely
const fmt = (n, digits = 8) => {
  if (!isFinite(n)) return "-";
  const val = Number(n);
  if (val === 0) return "0";
  return val.toFixed(digits).replace(/\.?0+$/, "");
};

export default function Fees() {
  // ✅ SEO activation
  useSEO({
    title: "Fees & Rewards | 1CryptoX — Transparent Trading Costs",
    description:
      "Discover 1CryptoX's ultra-competitive fee structure: near-zero Spot & Futures fees, transparent AI Trading costs, flat 5 USDT withdrawals, and daily Luck Wheel rewards.",
    keywords: [
      "1CryptoX",
      "crypto trading fees",
      "spot trading",
      "futures trading",
      "AI trading",
      "luck wheel rewards",
      "withdrawal fee",
      "low fee exchange",
      "binance style",
    ],
    canonical: "https://1cryptox.com/fees",
    og: {
      title: "1CryptoX Fees & Rewards — Low Fees, Clear Terms",
      description:
        "Spot & Futures near-zero fees, AI trading transparency, flat 5 USDT withdrawals, and daily Luck Wheel rewards.",
      url: "https://1cryptox.com/fees",
      image: "https://1cryptox.com/og/fees-banner.jpg",
      siteName: "1CryptoX",
      type: "website",
    },
    twitter: {
      card: "summary_large_image",
      title: "1CryptoX Fees & Rewards",
      description:
        "Transparent fees across Spot, Futures, and AI trading. Flat 5 USDT withdrawals. Daily Luck Wheel rewards.",
      image: "https://1cryptox.com/og/fees-banner.jpg",
    },
  });

  // ✅ JSON-LD Schema (structured data)
  useJsonLD("fees-structured-data", {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "1CryptoX Fees & Rewards",
    description:
      "Transparent crypto trading fees at 1CryptoX. Spot & Futures trading near zero, AI trading small service fee, fixed 5 USDT withdrawal, and daily Luck Wheel rewards.",
    url: "https://1cryptox.com/fees",
    publisher: {
      "@type": "Organization",
      name: "1CryptoX Global",
      logo: "https://1cryptox.com/logo.png",
    },
    mainEntity: {
      "@type": "Table",
      name: "Withdrawal Fees Table",
      about:
        "Flat 5 USDT per withdrawal across major networks with live equivalent calculation.",
    },
  });

  // State for 5 USDT equivalent calculation
  const [loadingMap, setLoadingMap] = useState({});
  const [resultMap, setResultMap] = useState({});
  const [errorMap, setErrorMap] = useState({});

  const handleCalc5USDT = async (row) => {
    const { cgId, asset } = row;
    try {
      setErrorMap((m) => ({ ...m, [asset]: null }));
      setLoadingMap((m) => ({ ...m, [asset]: true }));
      setResultMap((m) => ({ ...m, [asset]: undefined }));

      const url = `https://api.coingecko.com/api/v3/simple/price?ids=${encodeURIComponent(
        cgId
      )}&vs_currencies=usd`;
      const res = await fetch(url, { method: "GET" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const usd = data?.[cgId]?.usd;
      if (!usd || !isFinite(usd)) throw new Error("Invalid price");

      // amount of the asset equal to 5 USDT (≈ 5 USD)
      const amount = 5 / usd;
      setResultMap((m) => ({ ...m, [asset]: amount }));
    } catch (e) {
      setErrorMap((m) => ({
        ...m,
        [asset]: "Failed to fetch price. Please try again.",
      }));
    } finally {
      setLoadingMap((m) => ({ ...m, [asset]: false }));
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        px: { xs: 2, md: 4 },
        py: { xs: 4, md: 6 },
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      <Container maxWidth="lg" disableGutters>
        {/* Header */}
        <Card
          sx={{
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            backdropFilter: "blur(8px)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 900, color: TEXT, mb: 1 }}
            >
              Fees & Rewards
            </Typography>
            <Typography sx={{ color: MUTED }}>
              1CryptoX adopts an ultra-competitive model:{" "}
              <b>Spot & Perpetuals are nearly fee-free</b>, while{" "}
              <b>AI-powered trading</b> applies a small service fee to cover
              algorithmic computing costs and automation. Withdrawals use a flat
              fee model. To offset costs, <b>Luck Wheel</b> and
              <b> Promote</b> programs deliver real rewards.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Chip
                icon={<CandlestickChartIcon />}
                label="Spot ≈ 0% maker/taker"
                sx={{
                  bgcolor: "rgba(255,255,255,0.06)",
                  color: TEXT,
                  border: `1px solid ${BORDER}`,
                }}
              />
              <Chip
                icon={<ShowChartIcon />}
                label="Futures ≈ 0% maker/taker"
                sx={{
                  bgcolor: "rgba(255,255,255,0.06)",
                  color: TEXT,
                  border: `1px solid ${BORDER}`,
                }}
              />
              <Chip
                icon={<InsightsIcon />}
                label="AI fee (transparent upfront)"
                sx={{
                  bgcolor: "rgba(240,185,11,0.12)",
                  color: ACCENT,
                  border: `1px solid rgba(240,185,11,0.25)`,
                  fontWeight: 700,
                }}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Spot / Futures */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ color: TEXT, fontWeight: 900, mb: 1 }}
                >
                  Spot Trading
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: MUTED }}>Maker</TableCell>
                      <TableCell sx={{ color: MUTED }}>Taker</TableCell>
                      <TableCell sx={{ color: MUTED }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ color: TEXT }}>
                        {SPOT_MAKER.toFixed(2)}%
                      </TableCell>
                      <TableCell sx={{ color: TEXT }}>
                        {SPOT_TAKER.toFixed(2)}%
                      </TableCell>
                      <TableCell sx={{ color: MUTED }}>
                        Zero-fee approach for core pairs; promos may apply.
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Alert
                  icon={<SecurityIcon fontSize="inherit" />}
                  sx={{
                    mt: 1.25,
                    bgcolor: "rgba(255,255,255,0.05)",
                    color: MUTED,
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  Advanced order types supported: limit/market/stop, IOC/FOK,
                  with pro-grade execution.
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ color: TEXT, fontWeight: 900, mb: 1 }}
                >
                  Perpetual Futures
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: MUTED }}>Maker</TableCell>
                      <TableCell sx={{ color: MUTED }}>Taker</TableCell>
                      <TableCell sx={{ color: MUTED }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ color: TEXT }}>
                        {FUTURES_MAKER.toFixed(2)}%
                      </TableCell>
                      <TableCell sx={{ color: TEXT }}>
                        {FUTURES_TAKER.toFixed(2)}%
                      </TableCell>
                      <TableCell sx={{ color: MUTED }}>
                        Funding applies per market conditions; no hidden fees.
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Alert
                  icon={<ShowChartIcon fontSize="inherit" />}
                  sx={{
                    mt: 1.25,
                    bgcolor: "rgba(255,255,255,0.05)",
                    color: MUTED,
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  Transparent funding & maintenance margin. Use risk controls to
                  avoid liquidation.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* AI Trade */}
        <Card sx={{ mt: 2, bgcolor: BG, border: `1px solid ${BORDER}` }}>
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <InsightsIcon sx={{ color: ACCENT }} />
              <Typography variant="h6" sx={{ color: TEXT, fontWeight: 900 }}>
                AI-Powered Trading — Service Fee
              </Typography>
            </Stack>
            <Typography sx={{ color: MUTED, mb: 1 }}>
              AI orders include a small service fee to cover model inference,
              signal validation, and automation orchestration. Fees are{" "}
              <b>always shown before confirmation</b> for full transparency.
            </Typography>
            <Chip
              label={AI_TRADE_FEE_DESC}
              sx={{
                bgcolor: "rgba(240,185,11,0.12)",
                color: ACCENT,
                border: `1px solid rgba(240,185,11,0.25)`,
                fontWeight: 700,
              }}
            />
          </CardContent>
        </Card>

        {/* Withdrawals — Flat 5 USDT & Calculator */}
        <Card sx={{ mt: 2, bgcolor: BG, border: `1px solid ${BORDER}` }}>
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <CurrencyExchangeIcon sx={{ color: ACCENT }} />
              <Typography variant="h6" sx={{ color: TEXT, fontWeight: 900 }}>
                Withdrawal Fees — Flat 5 USDT
              </Typography>
            </Stack>
            <Typography sx={{ color: MUTED, mb: 1 }}>
              A flat withdrawal fee of <b>5 USDT</b> applies for the assets
              below (including <b>LTC</b>). Network confirmations and ETA depend
              on chain conditions.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: MUTED }}>Asset</TableCell>
                  <TableCell sx={{ color: MUTED }}>Network</TableCell>
                  <TableCell sx={{ color: MUTED }}>Fee</TableCell>
                  <TableCell sx={{ color: MUTED }}>
                    5 USDT ≈ (Amount in Asset)
                  </TableCell>
                  <TableCell sx={{ color: MUTED }}></TableCell>
                  <TableCell sx={{ color: MUTED }}>ETA</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {WITHDRAW_ROWS.map((r) => {
                  const isLoading = !!loadingMap[r.asset];
                  const result = resultMap[r.asset];
                  const err = errorMap[r.asset];
                  return (
                    <TableRow key={r.asset}>
                      <TableCell sx={{ color: TEXT }}>{r.asset}</TableCell>
                      <TableCell sx={{ color: TEXT }}>{r.network}</TableCell>
                      <TableCell sx={{ color: TEXT }}>5 USDT</TableCell>
                      <TableCell sx={{ color: TEXT }}>
                        {isLoading ? (
                          "Fetching..."
                        ) : err ? (
                          <Typography sx={{ color: "#ff8a80" }}>
                            {err}
                          </Typography>
                        ) : result !== undefined ? (
                          `${fmt(result)} ${r.asset.split(" ")[0]}`
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell sx={{ color: TEXT }}>
                        <Button
                          variant="outlined"
                          onClick={() => handleCalc5USDT(r)}
                          disabled={isLoading}
                          sx={{
                            color: ACCENT,
                            borderColor: "rgba(240,185,11,0.4)",
                            "&:hover": {
                              borderColor: ACCENT,
                              bgcolor: "rgba(240,185,11,0.08)",
                            },
                            fontWeight: 700,
                          }}
                        >
                          {isLoading ? (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <CircularProgress size={16} />
                              <span>Loading</span>
                            </Stack>
                          ) : (
                            "Calc 5 USDT Equivalent"
                          )}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ color: TEXT }}>{r.eta}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Alert
              sx={{
                mt: 1.25,
                bgcolor: "rgba(255,255,255,0.05)",
                color: MUTED,
                border: `1px solid ${BORDER}`,
              }}
            >
              Tip: Add addresses to your allow-list for faster processing and
              enhanced security.
            </Alert>
          </CardContent>
        </Card>

        {/* Rewards: Luck Wheel & Promote */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                bgcolor: BG,
                border: `1px solid ${BORDER}`,
                height: "100%",
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center">
                  <RedeemIcon sx={{ color: ACCENT }} />
                  <Typography
                    variant="h6"
                    sx={{ color: TEXT, fontWeight: 900 }}
                  >
                    Luck Wheel — Spot-Linked Booster
                  </Typography>
                </Stack>

                {/* Copy per spec */}
                <Typography sx={{ color: MUTED, mt: 1 }}>
                  The <b>Luck Wheel</b> is tied to your <b>Spot Wallet</b> and
                  is designed to help
                  <b> cover AI Trade fees</b>. You can perform{" "}
                  <b>one spin every 24 hours</b>. Rewards are{" "}
                  <b>percentages of your total Spot balance</b>: 1%, 3%, 5%,
                  10%, or 100%. The <b>larger your Spot balance</b>, the{" "}
                  <b>higher your winning chances</b> on bigger tiers.
                </Typography>
                <Typography sx={{ color: MUTED, mt: 1 }}>
                  Winnings are credited to your Spot Wallet.
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 1, flexWrap: "wrap" }}
                >
                  {[
                    "1% of Spot",
                    "3% of Spot",
                    "5% of Spot",
                    "10% of Spot",
                    "100% of Spot",
                  ].map((t) => (
                    <Chip
                      key={t}
                      label={t}
                      sx={{
                        bgcolor: "rgba(240,185,11,0.10)",
                        color: ACCENT,
                        border: `1px solid rgba(240,185,11,0.25)`,
                        fontWeight: 700,
                      }}
                    />
                  ))}
                </Stack>

                <Button
                  href="/luck-wheel"
                  sx={{ mt: 1, color: ACCENT, fontWeight: 800 }}
                >
                  Explore Luck Wheel
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                bgcolor: BG,
                border: `1px solid ${BORDER}`,
                height: "100%",
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center">
                  <StarIcon sx={{ color: ACCENT }} />
                  <Typography
                    variant="h6"
                    sx={{ color: TEXT, fontWeight: 900 }}
                  >
                    Promote & Earn — Creator Rewards
                  </Typography>
                </Stack>
                <Typography sx={{ color: MUTED, mt: 1 }}>
                  Create content about 1CryptoX and earn rewards (USDT /
                  vouchers). High-quality videos and tutorials may unlock{" "}
                  <b>VIP boosts</b> and <b>fee rebates</b> for your audience.
                </Typography>
                <Button
                  href="/promote-earn"
                  sx={{ mt: 1, color: ACCENT, fontWeight: 800 }}
                >
                  Start Promoting
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Notes */}
        <Card sx={{ mt: 2, bgcolor: SURFACE, border: `1px solid ${BORDER}` }}>
          <CardContent>
            <Typography sx={{ color: TEXT, fontWeight: 900, mb: 1 }}>
              Notes & Disclaimers
            </Typography>
            <Divider sx={{ borderColor: BORDER, mb: 1 }} />
            <Stack spacing={0.75}>
              <Typography sx={{ color: MUTED }}>
                • Fees may vary by pair, volatility, or promotional events.
                Always review the final quote on the order/withdraw screen
                before confirming.
              </Typography>
              <Typography sx={{ color: MUTED }}>
                • AI service fees are disclosed <b>upfront</b> and may depend on
                strategy type, position size, and compute usage.
              </Typography>
              <Typography sx={{ color: MUTED }}>
                • Funding applies on Perpetual Futures per market conditions;
                see each market’s details.
              </Typography>
              <Typography sx={{ color: MUTED }}>
                • Regional restrictions may apply. Ensure compliance with your
                local regulations.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
